/**
 * Bookings — a read-only month view over ServiceM8's schedule.
 *
 * Dressed in the Jobs Overview furniture (`Kpi` tiles, an `overview-band`
 * filter row) so moving between the two costs a reader nothing. The calendar
 * itself stays bespoke: Jobs has no equivalent to copy.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  groupJobsIntoVisits,
  jobMatchesSearch,
  type InspectionJob,
  type JobVisit,
  type SchedulingOverview,
} from '@cer-autofill/shared';
import { fetchSchedulingOverview, apiErrorReason } from '../api.js';
import { jobStatusLabel } from '../format.js';
import { Kpi } from '../components/Kpi.js';
import { FilterSelect } from '../components/FilterSelect.js';

const todayMonth = () => new Date().toISOString().slice(0, 7);

export function SchedulingScreen() {
  const [month, setMonth] = useState(todayMonth);
  const [data, setData] = useState<SchedulingOverview | null>(null);
  const [clientId, setClientId] = useState('');
  const [inspectorId, setInspectorId] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const debouncedSearch = useDebounced(search, 200);
  useEffect(() => {
    let current = true;
    setError(null);
    setLoading(true);
    void fetchSchedulingOverview(month, clientId, inspectorId)
      .then((next) => {
        if (current) setData(next);
      })
      .catch((e: unknown) => {
        if (current) setError(apiErrorReason(e));
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [month, clientId, inspectorId, refresh]);
  useEffect(() => {
    const refreshOnFocus = () => setRefresh((value) => value + 1);
    window.addEventListener('focus', refreshOnFocus);
    return () => window.removeEventListener('focus', refreshOnFocus);
  }, []);
  const days = useMemo(() => calendarDays(month), [month]);
  /**
   * Calendar entries are VISITS, not job rows.
   *
   * A combined PV+battery site is two job records but ONE ServiceM8 booking —
   * one inspector, one arrival time, one address. Bucketing per record put two
   * blocks on the day for a single appointment, which reads as double the work
   * and cannot be reconciled against the day's real bookings; Availability's
   * "From ServiceM8" panel lists that same visit once, under its combined
   * purchase order.
   *
   * Grouped before bucketing, so a pair whose halves somehow carry different
   * scheduled dates still resolves to one entry, on the primary's date.
   */
  const byDate = useMemo(() => {
    const map = new Map<string, JobVisit[]>();
    const needle = debouncedSearch.trim();
    for (const visit of groupJobsIntoVisits(data?.jobs ?? [])) {
      if (needle && !visitMatchesSearch(visit, needle)) continue;
      const date = visit.primary.scheduledDate ?? visit.partner?.scheduledDate;
      if (!date) continue;
      map.set(date, [...(map.get(date) ?? []), visit]);
    }
    return map;
  }, [data, debouncedSearch]);
  const m = data?.metrics;
  return (
    <main className="screen scheduling-screen">
      <div className="screen__header">
        <div>
          <h2>Bookings</h2>
          <p className="subtitle">Read-only summary of ServiceM8 bookings</p>
        </div>
        <button type="button" onClick={() => setRefresh((value) => value + 1)} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      {error && <p className="error">{error}</p>}

      <section
        className="overview-kpis overview-kpis--five"
        aria-label="Monthly scheduling metrics"
      >
        <Kpi label="Booked" value={m?.booked} unit="Jobs" tone="blue" icon="calendar" />
        <Kpi label="Upcoming" value={m?.upcoming} unit="Jobs" tone="violet" icon="clock" />
        <Kpi
          label="Completed Onsite"
          value={m?.completedOnsite}
          unit="Jobs"
          tone="green"
          icon="check"
        />
        <Kpi
          label="Overdue"
          value={m?.overdue}
          unit="Jobs"
          tone={m?.overdue ? 'red' : 'green'}
          icon="alert"
          footnote={m && !m.overdue ? { text: 'Nothing overdue', tone: 'good' } : undefined}
        />
        <Kpi
          label="Unassigned"
          value={m?.unassigned}
          unit="Jobs"
          tone={m?.unassigned ? 'amber' : 'green'}
          icon="users"
          footnote={m?.unassigned ? { text: 'Needs an inspector', tone: 'warn' } : undefined}
        />
        <Kpi label="Cancelled" value={m?.cancelled} unit="Jobs" tone="neutral" icon="minus" />
        <Kpi
          label="PV Reports Expected"
          value={m?.pvReportsExpected}
          unit="Reports"
          tone="neutral"
          icon="clipboard"
        />
        <Kpi
          label="BTY Reports Expected"
          value={m?.btyReportsExpected}
          unit="Reports"
          tone="neutral"
          icon="clipboard"
        />
        <Kpi
          label="Additional Information"
          value={m?.informationRequired}
          unit="Reports"
          tone={m?.informationRequired ? 'amber' : 'green'}
          icon="alert-circle"
          footnote={
            m && !m.informationRequired ? { text: 'None waiting', tone: 'good' } : undefined
          }
        />
        <Kpi
          label="Return To Site"
          value={m?.revisitRequired}
          unit="Reports"
          tone={m?.revisitRequired ? 'red' : 'green'}
          icon="flag"
          footnote={
            m
              ? m.revisitRequired
                ? { text: 'Needs attention', tone: 'risk' }
                : { text: 'None outstanding', tone: 'good' }
              : undefined
          }
        />
      </section>

      <section className="overview-band overview-filters" aria-label="Booking filters">
        <div className="filter-grid filter-grid--bookings">
          <label>
            <span>Search</span>
            <input
              type="search"
              value={search}
              placeholder="Job, homeowner or address"
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <label>
            <span>Month</span>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          </label>
          <FilterSelect
            label="Client"
            value={clientId}
            onChange={setClientId}
            options={(data?.options.clients ?? []).map((client) => [
              client.clientId,
              client.clientName,
            ])}
            allLabel="All clients"
          />
          <FilterSelect
            label="Inspector"
            value={inspectorId}
            onChange={setInspectorId}
            options={(data?.options.inspectors ?? []).map((inspector) => [inspector, inspector])}
            allLabel="All inspectors"
          />
        </div>
        <div className="filter-actions">
          <button
            type="button"
            className="ghost-btn"
            onClick={() => {
              setSearch('');
              setClientId('');
              setInspectorId('');
            }}
          >
            Clear
          </button>
        </div>
      </section>

      <section className="overview-band schedule-summary">
        <div>
          <h3>Inspector workload</h3>
          <p>
            {data?.inspectors.map((i) => `${i.inspectorId}: ${i.booked}`).join(' · ') ||
              'No assigned bookings'}
          </p>
        </div>
        <p className="muted">Bookings are changed in ServiceM8, not here.</p>
      </section>
      {data?.qaExceptions.length ? (
        <section className="overview-band schedule-exceptions">
          <div className="band-heading">
            <h3>QA actions affecting scheduling</h3>
            <span>{data.qaExceptions.length} open</span>
          </div>
          {data.qaExceptions.map((item) => (
            <Link
              key={`${item.jobId}:${item.submittedAt}`}
              to={`/reports/${encodeURIComponent(item.jobId)}/${encodeURIComponent(item.submittedAt)}`}
            >
              <strong>{item.jobId}</strong>
              <span>
                {item.kind === 'revisit-required' ? 'Return to site' : 'Additional information'} ·{' '}
                {item.inspectorId}
              </span>
            </Link>
          ))}
        </section>
      ) : null}
      <section className="schedule-calendar" aria-label={`${month} calendar`}>
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
          <strong className="schedule-calendar__weekday" key={day}>
            {day}
          </strong>
        ))}
        {days.map(({ date, inMonth }) => (
          <div className={`schedule-day ${inMonth ? '' : 'schedule-day--outside'}`} key={date}>
            <span className="schedule-day__number">{Number(date.slice(-2))}</span>
            {(byDate.get(date) ?? []).map((visit) => (
              <Link
                className={`schedule-event schedule-event--${eventTone(visit.primary)}`}
                to={`/jobs/${encodeURIComponent(visit.primary.jobId)}`}
                key={visit.visitId}
                title={`${visit.primary.address.suburb} · ${jobStatusLabel(visit.primary.status)}${
                  visit.partner ? ' · one visit, two inspections' : ''
                }`}
              >
                <strong>{visit.purchaseOrderNumber}</strong>
                <span>
                  {visit.primary.inspectorId ?? 'Unassigned'} · {visit.primary.address.suburb}
                </span>
              </Link>
            ))}
          </div>
        ))}
      </section>
    </main>
  );
}

function visitMatchesSearch(visit: JobVisit, search: string): boolean {
  return (
    jobMatchesSearch(visit.primary, search) ||
    (visit.partner ? jobMatchesSearch(visit.partner, search) : false)
  );
}

/** Tiny debounce hook — same as Reports, no extra deps. */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

function eventTone(job: InspectionJob): string {
  if (job.status === 'cancelled') return 'cancelled';
  if (['submitted', 'qa-passed', 'approved-for-upload', 'uploaded', 'closed'].includes(job.status))
    return 'complete';
  if (!job.inspectorId) return 'attention';
  return 'booked';
}
function calendarDays(month: string): Array<{ date: string; inMonth: boolean }> {
  const [year, number] = month.split('-').map(Number) as [number, number];
  const first = new Date(Date.UTC(year, number - 1, 1));
  const offset = (first.getUTCDay() + 6) % 7;
  const start = new Date(first);
  start.setUTCDate(1 - offset);
  return Array.from({ length: 42 }, (_, index) => {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + index);
    const date = d.toISOString().slice(0, 10);
    return { date, inMonth: date.startsWith(month) };
  });
}
